import * as yup from 'yup'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import {
    Stack,
    Heading,
    Text,
    Avatar,
    Box,
    Card,
    CardBody,
    AvatarBadge,
    Button,
    useDisclosure,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    FormControl,
    FormLabel,
    Input,
    FormHelperText,
    FormErrorMessage
} from '@chakra-ui/react'
import { ParentProfile } from '../type'
import {
    MAXIMUM_IMAGE_SIZE,
    resizeAndUploadImage
} from '@/shared/utils/imageUploadUtility'
import { useAlertService } from '@/shared/lib/useAlertService'
import { AuthService } from '@/shared/api/rest/services/auth.service'
import { useMutation } from '@tanstack/react-query'
import { useAuth } from '@/shared/context/AuthContext'

type ProfileHeaderProps = {
    profile?: ParentProfile | null
}

function firstPicture(value: unknown): File | undefined {
    return Array.isArray(value) && value[0] instanceof File ? value[0] : undefined
}

// Define the validation schema for uploading a picture
const pictureUploadSchema = yup.object().shape({
    picture: yup
        .mixed()
        .test('noImage', 'Image is required', (value: unknown) => {
            return Boolean(firstPicture(value)?.size)
        })
        .test('fileSize', 'The file is too large', (value: unknown) => {
            const file = firstPicture(value)
            return Boolean(file && file.size <= MAXIMUM_IMAGE_SIZE)
        })
})

type PictureFormValue = yup.InferType<typeof pictureUploadSchema>

function renderProfileDetails(profile?: ParentProfile | null) {
    if (!profile) {
        return (
            <Stack gap={1}>
                <Heading size="sm" textColor="brand.400">
                    User
                </Heading>
                <Text textColor="gray">No email available</Text>
            </Stack>
        )
    }

    return (
        <Stack gap={1}>
            <Heading size="sm" textColor="brand.400">
                {`${profile.firstName} ${profile.lastName}`}
            </Heading>
            <Text textColor="gray">{profile.email}</Text>
        </Stack>
    )
}

/** Profile Header Component */
export default function ProfileHeader({ profile }: ProfileHeaderProps) {
    const alertService = useAlertService()
    const { refreshProfile, user } = useAuth()
    const { isOpen, onOpen, onClose } = useDisclosure()
    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting },
        reset
    } = useForm<PictureFormValue>({
        resolver: yupResolver(pictureUploadSchema)
    })

    // REST mutation for updating the parent's profile picture.
    const { mutateAsync: updateProfile, isPending: loading } = useMutation({
        mutationFn: (picture: string) => AuthService.updateProfile({ picture }),
        onSuccess: () => {
            refreshProfile()
        },
        onError: (error: Error) => {
            alertService.error(error.message)
        }
    })

    const onSubmit = async (formValue: PictureFormValue) => {
        try {
            const pictureUrl = await resizeAndUploadImage(formValue, '')
            await updateProfile(pictureUrl)
            onClose()
            reset()
        } catch (error: unknown) {
            if (error instanceof Error) {
                alertService.error(error.message, error.name)
            } else {
                alertService.error('An unknown error occurred', 'UnknownError')
            }
        }
    }

    return (
        <>
            <Box mb={9}>
                <Heading size="md" mb={4}>
                    My Profile
                </Heading>
                <Card variant="outline">
                    <CardBody>
                        <Stack direction={['column', 'row']} alignItems="center">
                            <Avatar size="xl" src={user?.imageUrl}>
                                <AvatarBadge as={Button} bg="brand" onClick={onOpen}>
                                    Edit
                                </AvatarBadge>
                            </Avatar>
                            {renderProfileDetails(profile)}
                        </Stack>
                    </CardBody>
                </Card>
            </Box>

            <Modal isOpen={isOpen} onClose={onClose}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Edit Profile Picture</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <form onSubmit={handleSubmit(onSubmit)}>
                            <FormControl>
                                <FormLabel>Upload new profile picture</FormLabel>
                                <Input
                                    type="file"
                                    accept="image/*"
                                    {...register('picture')}
                                />
                                <FormErrorMessage>
                                    {errors.picture && errors.picture.message}
                                </FormErrorMessage>
                                <FormHelperText>
                                    Maximum image size allowed is 4MB 😉
                                </FormHelperText>
                            </FormControl>
                        </form>
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="ghost" mr={3} onClick={onClose}>
                            Cancel
                        </Button>
                        <Button
                            colorScheme="brand"
                            onClick={handleSubmit(onSubmit)}
                            isLoading={isSubmitting || loading}
                        >
                            Save Changes
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </>
    )
}
